import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdminEmptyState } from "@/project/jain_elibrary-admin/components/shared/AdminEmptyState";
import { AdminPageTitle } from "@/project/jain_elibrary-admin/components/shared/AdminPageTitle";
import { AdminPaginationBar } from "@/project/jain_elibrary-admin/components/shared/AdminPaginationBar";
import { AdminTableSkeleton } from "@/project/jain_elibrary-admin/components/shared/AdminTableSkeleton";
import {
  adminService,
  RegisteredUser,
} from "@/project/jain_elibrary-admin/services/adminService";
import { getAdminAccessToken } from "@lib/storeManager";
import { jwtDecode } from "jwt-decode";
import {
  Edit2,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  Shield,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

interface DecodedToken {
  email?: string;
  user_id?: number;
}

function getCurrentAdminEmail(): string {
  try {
    const token = getAdminAccessToken();
    if (!token) {
      return "";
    }
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.email ?? "";
  } catch {
    return "";
  }
}

const AdminsManagement = () => {
  const [admins, setAdmins] = useState<RegisteredUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  const currentAdminEmail = getCurrentAdminEmail();

  // Create modal
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [isCreating, setIsCreating] = useState(false);

  // Edit modal
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<RegisteredUser | null>(
    null
  );
  const [editForm, setEditForm] = useState<Partial<RegisteredUser>>({});
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchAdmins = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getAdmins({
          page,
          page_size: pagination.pageSize,
          search,
        });
        setAdmins(data.results ?? []);
        setPagination(prev => ({ ...prev, count: data.count ?? 0, page }));
      } catch (error: any) {
        toast.error(error?.response?.data?.detail ?? "Failed to load admins.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchAdmins(1, searchTerm);
  };

  // Create
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !createForm.email ||
      !createForm.password ||
      !createForm.first_name ||
      !createForm.last_name
    ) {
      toast.error("All fields are required.");
      return;
    }
    try {
      setIsCreating(true);
      await adminService.createAdmin(createForm);
      toast.success("Admin created successfully.");
      setIsCreateOpen(false);
      setCreateForm({ first_name: "", last_name: "", email: "", password: "" });
      fetchAdmins(pagination.page, searchTerm);
    } catch (error: any) {
      const msg =
        error?.response?.data?.email?.[0] ??
        error?.response?.data?.detail ??
        error?.response?.data?.message ??
        "Failed to create admin.";
      toast.error(msg);
    } finally {
      setIsCreating(false);
    }
  };

  // Edit
  const handleEditClick = (admin: RegisteredUser) => {
    setSelectedAdmin(admin);
    setEditForm({
      first_name: admin.first_name ?? "",
      last_name: admin.last_name ?? "",
      phone: admin.phone ?? admin.phone_number ?? "",
      address: admin.address ?? "",
      city: admin.city ?? "",
      state: admin.state ?? "",
      country: admin.country ?? "",
      postal_code: admin.postal_code ?? "",
      bio: admin.bio ?? "",
      is_active: admin.is_active !== false,
      download_limit: admin.download_limit ?? 25,
    });
    setIsEditOpen(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAdmin) {
      return;
    }
    try {
      setIsUpdating(true);
      await adminService.updateAdmin(selectedAdmin.id, editForm);
      toast.success("Admin updated successfully.");
      setIsEditOpen(false);
      fetchAdmins(pagination.page, searchTerm);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? "Failed to update admin.");
    } finally {
      setIsUpdating(false);
    }
  };

  // Delete
  const handleDelete = async (admin: RegisteredUser) => {
    if (admin.email === currentAdminEmail) {
      toast.error("You cannot delete your own account.");
      return;
    }
    if (
      !window.confirm(`Delete admin "${admin.email}"? This cannot be undone.`)
    ) {
      return;
    }

    // Optimistic update — remove immediately from UI
    setAdmins(prev => prev.filter(a => a.id !== admin.id));
    setPagination(prev => ({ ...prev, count: Math.max(0, prev.count - 1) }));

    try {
      await adminService.deleteAdmin(admin.id);
      toast.success("Admin deleted successfully.");
    } catch (error: any) {
      // Revert on failure
      setAdmins(prev => [admin, ...prev]);
      setPagination(prev => ({ ...prev, count: prev.count + 1 }));
      toast.error(error?.response?.data?.detail ?? "Failed to delete admin.");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <AdminPageTitle
          title="Admins"
          description="Manage admin accounts. Admins can log in and manage the library."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative min-w-[12rem]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search admins…"
                    className="pl-9"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  variant="secondary"
                  className="shrink-0 bg-[#533a1d] text-white hover:bg-[#533a1d]/90"
                >
                  Search
                </Button>
              </form>
              <Button onClick={() => setIsCreateOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" /> Add Admin
              </Button>
            </div>
          }
        />

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          {loading && admins.length === 0 ? (
            <AdminTableSkeleton rows={8} columns={5} className="border-0" />
          ) : null}
          {!(loading && admins.length === 0) && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading && admins.length > 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-2 text-center text-xs text-muted-foreground"
                      >
                        <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />{" "}
                        Updating…
                      </td>
                    </tr>
                  ) : null}
                  {!loading && admins.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-6">
                        <AdminEmptyState
                          icon={Shield}
                          title="No admins found"
                          description="Add an admin using the button above."
                        />
                      </td>
                    </tr>
                  ) : null}
                  {admins.map(admin => (
                    <tr
                      key={admin.id}
                      className="transition-colors hover:bg-muted/30"
                    >
                      <td className="px-4 py-3 font-medium">{admin.id}</td>
                      <td className="px-4 py-3">
                        {`${admin.first_name ?? ""} ${admin.last_name ?? ""}`.trim() ||
                          "—"}
                        {admin.email === currentAdminEmail && (
                          <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                            You
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{admin.email}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            admin.is_active !== false
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                          }`}
                        >
                          {admin.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-primary hover:bg-primary/10"
                            onClick={() => handleEditClick(admin)}
                            title="Edit Admin"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(admin)}
                            title={
                              admin.email === currentAdminEmail
                                ? "Cannot delete your own account"
                                : "Delete Admin"
                            }
                            disabled={admin.email === currentAdminEmail}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <AdminPaginationBar
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalCount={pagination.count}
          loading={loading}
          onPageChange={p => void fetchAdmins(p, searchTerm)}
          onPageSizeChange={size =>
            setPagination(prev => ({ ...prev, pageSize: size, page: 1 }))
          }
        />
      </div>

      {/* Create Admin Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md border-none p-0 shadow-2xl">
          <DialogHeader className="border-b bg-muted/30 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Shield className="h-5 w-5" />
              </div>
              <DialogTitle className="text-xl font-bold">
                Add New Admin
              </DialogTitle>
            </div>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <ScrollArea className="max-h-[70vh] px-6 py-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-first-name">First Name *</Label>
                    <Input
                      id="create-first-name"
                      value={createForm.first_name}
                      onChange={e =>
                        setCreateForm({
                          ...createForm,
                          first_name: e.target.value,
                        })
                      }
                      placeholder="John"
                      className="bg-muted/30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-last-name">Last Name *</Label>
                    <Input
                      id="create-last-name"
                      value={createForm.last_name}
                      onChange={e =>
                        setCreateForm({
                          ...createForm,
                          last_name: e.target.value,
                        })
                      }
                      placeholder="Doe"
                      className="bg-muted/30"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-email">Email *</Label>
                  <Input
                    id="create-email"
                    type="email"
                    value={createForm.email}
                    onChange={e =>
                      setCreateForm({ ...createForm, email: e.target.value })
                    }
                    placeholder="admin@example.com"
                    className="bg-muted/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-password">Password *</Label>
                  <Input
                    id="create-password"
                    type="password"
                    value={createForm.password}
                    onChange={e =>
                      setCreateForm({ ...createForm, password: e.target.value })
                    }
                    placeholder="Min. 8 characters"
                    className="bg-muted/30"
                  />
                </div>
              </div>
            </ScrollArea>
            <DialogFooter className="border-t bg-muted/30 px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreating}
                className="min-w-[100px]"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create Admin"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg border-none p-0 shadow-2xl">
          <DialogHeader className="border-b bg-muted/30 px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Edit2 className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-xl font-bold">
                  Edit Admin
                </DialogTitle>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {selectedAdmin?.email}
                </p>
              </div>
            </div>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 px-6 py-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-first-name">First Name</Label>
                  <Input
                    id="edit-first-name"
                    value={editForm.first_name ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, first_name: e.target.value })
                    }
                    placeholder="First name"
                    className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-last-name">Last Name</Label>
                  <Input
                    id="edit-last-name"
                    value={editForm.last_name ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, last_name: e.target.value })
                    }
                    placeholder="Last name"
                    className="border-muted-foreground/20 bg-muted/30 transition-colors focus:bg-background"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={selectedAdmin?.email ?? ""}
                    readOnly
                    disabled
                    className="cursor-not-allowed bg-muted/50 pl-10 text-muted-foreground"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={editForm.phone ?? ""}
                    onChange={e =>
                      setEditForm({ ...editForm, phone: e.target.value })
                    }
                    placeholder="Phone number"
                    className="border-muted-foreground/20 bg-muted/30 pl-10 transition-colors focus:bg-background"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Account Status</Label>
                <div className="flex items-center gap-3 rounded-md border border-muted-foreground/10 bg-muted/30 p-3">
                  <Switch
                    id="edit-is-active"
                    checked={editForm.is_active === true}
                    onCheckedChange={checked =>
                      setEditForm(prev => ({ ...prev, is_active: checked }))
                    }
                  />
                  <Label
                    htmlFor="edit-is-active"
                    className="cursor-pointer font-normal"
                  >
                    Account is{" "}
                    {editForm.is_active === true ? "Active" : "Inactive"}
                  </Label>
                  <Badge
                    variant={
                      editForm.is_active === true ? "default" : "secondary"
                    }
                    className="pointer-events-none ml-auto"
                  >
                    {editForm.is_active === true ? "Active" : "Disabled"}
                  </Badge>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 border-t bg-muted/30 px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUpdating}
                className="min-w-[110px]"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminsManagement;
